import React, { useState, useEffect, useRef, } from "react";
import { useTheme } from "@mui/material";
import styled from "@xstyled/styled-components";

import { tokens } from "../../utils/theme";
import {
    ContentHeader,
    ContentTitleBlock,
    Chip,
    Subtitle,
    ContentActionsRow,
    BtnIcon,
    Btn,
    ScrollRailVertical,
    ScrollThumbVertical
} from "../../components/SharedPageStyles";
import { MARKET_STRUCTURE_DATA } from "../../constants/marketStructureData";
import { FiCheckCircle, FiCheck, FiInfo, FiChevronDown, FiChevronUp, FiArrowLeft, FiPlus, FiBookOpen, FiClock } from "react-icons/fi";
import { MdContentCopy, MdClose } from "react-icons/md";
import { TreeView, TreeContainer, } from "../../components/TreeView";
import { API_URL } from "../../config";
import TechnicalDictionaryModal from "./TechnicalDictionaryModal";
import { calculateSetupScore } from "../../utils/technicalCalculator";

// --- Custom Inline SVGs matching user uploaded image exactly ---

// 0. Macrostructural Context - Globe with latitude and longitude lines
const IconGlobe = ({ size = 20, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
);

// 1. Structural Bias - Compass with angled needle
const IconCompass = ({ size = 20, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" fill="currentColor" fillOpacity="0.25" />
    </svg>
);

// 2. Observation Zone - 4 rounded corner scan brackets with center dot
const IconScanTarget = ({ size = 20, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 9V6a2 2 0 0 1 2-2h3" />
        <path d="M14 4h3a2 2 0 0 1 2 2v3" />
        <path d="M19 15v3a2 2 0 0 1-2 2h-3" />
        <path d="M10 20H7a2 2 0 0 1-2-2v-3" />
        <circle cx="12" cy="12" r="2.2" fill="currentColor" />
    </svg>
);

// 3. Execution Zone - Circle dial with 4 radial ticks at 12, 3, 6, 9 o'clock & center dot
const IconExecutionDial = ({ size = 20, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <line x1="12" y1="3" x2="12" y2="6.5" />
        <line x1="12" y1="17.5" x2="12" y2="21" />
        <line x1="3" y1="12" x2="6.5" y2="12" />
        <line x1="17.5" y1="12" x2="21" y2="12" />
        <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    </svg>
);

// 4. Scenario & Key Levels - Target / Milestone Flag
const IconScenarioFlag = ({ size = 20, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" fill="currentColor" fillOpacity="0.2" />
        <line x1="4" y1="22" x2="4" y2="15" />
    </svg>
);

// --- Styled Components ---

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1100;
`;

const ModalBox = styled.div`
    background: ${props => props.theme.palette.mode === 'dark' ? '#1e2130' : '#ffffff'};
    padding: 24px;
    border-radius: 8px;
    width: 400px;
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
    text-align: center;
`;

const ModalTitle = styled.h3`
    margin: 0;
    font-size: 1.1rem;
    color: ${props => props.theme.palette.mode === 'dark' ? '#fff' : tokens(props.theme.palette.mode).grey[100]};
    margin-top: 0;
`;

const ModalText = styled.p`
    font-size: 1rem;
    color: ${props => props.theme.palette.mode === 'dark' ? '#b0bec5' : tokens(props.theme.palette.mode).grey[300]};
    margin-top: 12px;
    margin-bottom: 24px;
    line-height: 1.5;
`;

const ModalActions = styled.div`
    display: flex;
    justify-content: center;
    gap: 12px;
    margin-top: 24px;
`;

const PageContainer = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
`;

const ScrollableContent = styled.div`
    flex: 1;
    overflow-y: auto;
    padding-bottom: 80px;
    padding-right: 14px; /* Space for scrollbar */
    position: relative;

    /* Hide native scrollbar */
    &::-webkit-scrollbar {
        display: none;
    }
    scrollbar-width: none;
`;

const ContentWrapper = styled.div`
    max-width: 1800px;
    margin: 0 auto;
    width: 100%;
`;

const MainContentArea = styled.div`
    flex: 1;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;



const SplitLayout = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1.9fr) 1px minmax(0, 1fr);
    gap: 20px;
    align-items: stretch;
    margin-top: 4px;

    @media (max-width: 1250px) {
        grid-template-columns: minmax(0, 1.7fr) 1px minmax(0, 1.1fr);
        gap: 16px;
    }

    @media (max-width: 980px) {
        display: flex;
        flex-direction: column;
        gap: 20px;
    }
`;

const LeftGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;

    @media (max-width: 700px) {
        grid-template-columns: 1fr;
    }
`;

const VerticalDivider = styled.div`
    width: 1px;
    background: ${props => props.theme.palette.mode === 'dark'
        ? 'linear-gradient(180deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0.04) 100%)'
        : 'linear-gradient(180deg, rgba(0, 0, 0, 0.14) 0%, rgba(0, 0, 0, 0.03) 100%)'};
    align-self: stretch;
    border-radius: 1px;

    @media (max-width: 980px) {
        display: none;
    }
`;

const RightColumn = styled.div`
    display: flex;
    flex-direction: column;

    & > div {
        flex: 1;
        height: 100%;
    }
`;

// Top Tracker Workflow Bar
const WorkflowTrackerBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 20px;
    margin-top: 60px;
    margin-bottom: 22px;
    border-radius: 14px;
    background: ${props => props.theme.palette.mode === 'dark'
        ? 'linear-gradient(135deg, rgba(25, 30, 48, 0.6) 0%, rgba(18, 22, 35, 0.4) 100%)'
        : 'linear-gradient(135deg, rgba(255, 255, 255, 0.85) 0%, rgba(240, 244, 250, 0.6) 100%)'};
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'};
    backdrop-filter: blur(10px);
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.08);

    @media (max-width: 900px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }
`;

const TrackerPillsList = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const TrackerStepItem = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.82rem;
    font-weight: 600;
    color: ${props => props.isCompleted
        ? (props.theme.palette.mode === 'dark' ? '#34d399' : '#059669')
        : (props.theme.palette.mode === 'dark' ? '#94a3b8' : '#64748b')};
`;

const TrackerStepBadge = styled.span`
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.72rem;
    font-weight: 700;
    background: ${props => props.isCompleted
        ? (props.theme.palette.mode === 'dark' ? 'rgba(52, 211, 153, 0.2)' : 'rgba(5, 150, 105, 0.15)')
        : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)')};
    color: ${props => props.isCompleted
        ? (props.theme.palette.mode === 'dark' ? '#34d399' : '#059669')
        : 'inherit'};
    border: 1px solid ${props => props.isCompleted
        ? (props.theme.palette.mode === 'dark' ? 'rgba(52, 211, 153, 0.4)' : 'rgba(5, 150, 105, 0.3)')
        : 'transparent'};
`;

const TrackerStepTf = styled.span`
    padding: 2px 7px;
    border-radius: 6px;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.3px;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)'};
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
    color: ${props => props.theme.palette.mode === 'dark' ? '#e2e8f0' : '#1e293b'};
`;

const TrackerStepArrow = styled.span`
    opacity: 0.35;
    margin: 0 4px;
`;

const TrackerProgressCount = styled.div`
    font-size: 0.8rem;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: 12px;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(76, 206, 172, 0.15)' : 'rgba(15, 118, 110, 0.1)'};
    color: ${props => props.theme.palette.primary.main};
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(76, 206, 172, 0.3)' : 'rgba(15, 118, 110, 0.2)'};
`;

const SectionDivider = styled.div`
  height: 1px;
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
  margin: 8px 0 20px 0;
  width: 100%;
`;

// Styled components adapted from PositionCalculatorTab.jsx
const SectionBlock = styled.div`
    background: ${props => props.theme.palette.mode === 'dark'
        ? 'linear-gradient(145deg, rgba(30, 33, 48, 0.7) 0%, rgba(20, 23, 35, 0.4) 100%)'
        : 'linear-gradient(145deg, rgba(255, 255, 255, 0.9) 0%, rgba(240, 242, 245, 0.6) 100%)'};
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border-radius: 16px;
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(255, 255, 255, 0.6)'};
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    height: 100%;
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    position: relative;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
        border-color: ${props => props.theme.palette.mode === 'dark' ? 'rgba(76, 206, 172, 0.2)' : 'rgba(15, 118, 110, 0.2)'};
        background: ${props => props.theme.palette.mode === 'dark'
        ? 'linear-gradient(145deg, rgba(35, 38, 55, 0.8) 0%, rgba(25, 28, 42, 0.5) 100%)'
        : 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(245, 247, 250, 0.7) 100%)'};
    }
`;

const SectionTitle = styled.h3`
    margin: 0;
    font-size: 0.95rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${props => props.theme.palette.mode === 'dark' ? tokens(props.theme.palette.mode).grey[100] : tokens(props.theme.palette.mode).grey[100]};
    border-bottom: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'};
    padding-bottom: 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: row;
    gap: 8px;
    flex-wrap: wrap;
    row-gap: 10px;
`;

const CardIconBadge = styled.div`
    width: 38px;
    height: 38px;
    min-width: 38px;
    border-radius: 11px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${props => props.bgColor};
    color: ${props => props.iconColor};
    border: 1px solid ${props => props.borderColor};
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
    transition: transform 0.2s ease;

    &:hover {
        transform: scale(1.08);
    }
`;



const InfoIconWrapper = styled.div`
    color: ${props => tokens(props.theme.palette.mode).grey[400]};
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    border-radius: 50%;

    &:hover {
        color: ${props => tokens(props.theme.palette.mode).blueAccent[400]};
        background: rgba(255, 255, 255, 0.05);
    }
`;

const DetailsOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(35, 40, 59, 0.98)' : 'rgba(255, 255, 255, 0.98)'};
    backdrop-filter: blur(4px);
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
    z-index: 10;
    cursor: pointer;
    animation: fadeIn 0.2s ease-in-out;
    text-align: center;

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }
`;

const OverlayText = styled.div`
    font-size: 0.95rem;
    color: ${props => props.theme.palette.mode === 'dark' ? tokens(props.theme.palette.mode).grey[100] : tokens(props.theme.palette.mode).grey[800]};
    line-height: 1.5;
    font-weight: 500;
`;



const ConceptsContainer = styled.div`
    margin-bottom: 8px;
`;

const ChipsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 8px;
    align-items: center;
`;

const StyledChip = styled.div`
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 0.8rem;
    font-weight: 500;
    color: ${props => props.theme.palette.primary.main};
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(76, 206, 172, 0.15)' : 'rgba(15,118,110,0.1)'};
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(76, 206, 172, 0.3)' : 'rgba(15,118,110,0.2)'};
    display: inline-flex;
    align-items: center;
    gap: 6px;
`;

const ScrollableListContainer = styled.div`
    max-height: 180px;
    overflow-y: auto;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.5)'};
    border: 1px solid ${props => tokens(props.theme.palette.mode).grey[700]};
    border-radius: 6px;
    padding: 2px;
    display: flex;
    flex-direction: column;
    gap: 2px;

    /* Custom Scrollbar */
    &::-webkit-scrollbar {
        width: 6px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: ${props => tokens(props.theme.palette.mode).grey[400]};
        border-radius: 3px;
    }
    &::-webkit-scrollbar-thumb:hover {
        background: ${props => tokens(props.theme.palette.mode).grey[300]};
    }
`;

const DropdownTrigger = styled.div`
    padding: 10px 12px;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.7)'};
    border: 1px solid ${props => tokens(props.theme.palette.mode).grey[600]};
    border-radius: 6px;
    color: ${props => tokens(props.theme.palette.mode).grey[100]};
    font-size: 0.9rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    user-select: none;
    transition: all 0.2s;

    &:hover {
        border-color: ${props => props.theme.palette.primary.main};
    }
`;

const ListItem = styled.div`
    padding: 8px 12px;
    border-radius: 4px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: ${props => props.isEditing ? 'pointer' : 'default'};
    font-size: 0.85rem;
    color: ${props => tokens(props.theme.palette.mode).grey[100]};
    transition: all 0.2s;
    user-select: none;

    /* Selection State */
    ${props => props.selected ? `
        background: ${props.theme.palette.mode === 'dark' ? 'rgba(76, 206, 172, 0.15)' : 'rgba(15,118,110,0.1)'};
        color: ${props.theme.palette.primary.main};
        font-weight: 500;
    ` : `
        &:hover {
            background: ${props.isEditing ? (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)') : 'transparent'};
        }
    `}
`;

const CategoryHeader = styled.div`
    font-size: 0.75rem;
    font-weight: 700;
    color: ${props => props.theme.palette.mode === 'dark' ? tokens(props.theme.palette.mode).grey[300] : tokens(props.theme.palette.mode).grey[300]};
    text-transform: uppercase;
    padding: 8px 12px 4px 12px;
    letter-spacing: 0.5px;
    margin-top: 4px;

    &:first-child {
        margin-top: 0;
    }
`;


const NavContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)'};
    padding: 10px;
    border-radius: 8px;
    border: 1px solid ${props => tokens(props.theme.palette.mode).grey[800]};
    min-height: 180px;
`;

const NavHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 8px;
    border-bottom: 1px solid ${props => tokens(props.theme.palette.mode).grey[800]};
    margin-bottom: 4px;
`;

const NavTitle = styled.div`
    color: ${props => tokens(props.theme.palette.mode).grey[300]};
    font-size: 0.9rem;
    font-weight: 600;
`;

const NavBackBtn = styled.div`
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.8rem;
    color: ${props => tokens(props.theme.palette.mode).primary[400]};
    &:hover {
        text-decoration: underline;
    }
`;

const NavGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
`;

const NavTile = styled.div`
    background: ${props => props.isActive ?
        'rgba(68, 138, 255, 0.1)'
        : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)')};
    border: 1px solid ${props => props.isActive ?
        'rgba(68, 138, 255, 0.3)'
        : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)')};
    color: ${props => props.isActive ?
        '#448aff'
        : props.theme.palette.text.primary};
    padding: 12px;
    border-radius: 8px;
    cursor: pointer;
    text-align: center;
    font-size: 0.85rem;
    font-weight: 600;
    transition: all 0.2s ease-in-out;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 48px;
    position: relative;
    overflow: hidden;
    backdrop-filter: blur(4px);

    &:hover {
        background: ${props => props.isActive ?
        'rgba(68, 138, 255, 0.15)'
        : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)')};
        border-color: ${props => props.isActive ? 'rgba(68, 138, 255, 0.5)' : (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')};
    }

    &:active {
        transform: translateY(0);
    }
`;

const AddIconBtn = styled.div`
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    margin-left: 8px;
    transition: all 0.2s;
    &:hover {
        background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'};
        transform: scale(1.1);
    }
`;

// Timeframe Component Styles & Universal Multi-Timeframe Framework
const TimeframeControlContainer = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;
    z-index: ${props => props.isOpen ? 220 : 'auto'};
`;

const TimeframeTriggerButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 8px;
    font-size: 0.78rem;
    font-weight: 600;
    background: ${props => props.isOpen
        ? (props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)')
        : props.bgColor};
    color: ${props => props.accentColor};
    border: 1px solid ${props => props.isOpen ? props.accentColor : props.borderColor};
    letter-spacing: 0.3px;
    box-shadow: ${props => props.isOpen ? `0 0 10px ${props.borderColor}` : '0 2px 8px rgba(0, 0, 0, 0.05)'};
    white-space: nowrap;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    outline: none;

    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
        border-color: ${props => props.accentColor};
        background: ${props => props.theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(0, 0, 0, 0.05)'};
    }

    &:active {
        transform: translateY(0);
    }
`;

const TimeframeDropdownMenu = styled.div`
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    z-index: 250;
    min-width: 100px;
    background: ${props => props.isDark ? '#181b28' : '#ffffff'};
    border: 1px solid ${props => props.isDark ? 'rgba(255, 255, 255, 0.14)' : 'rgba(0, 0, 0, 0.12)'};
    border-radius: 10px;
    padding: 4px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
    animation: fadeInDropdown 0.14s ease;
    display: flex;
    flex-direction: column;
    gap: 2px;

    @keyframes fadeInDropdown {
        from { opacity: 0; transform: translateY(-4px); }
        to { opacity: 1; transform: translateY(0); }
    }
`;

const TimeframeOptionItem = styled.button`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 7px 12px;
    border-radius: 7px;
    font-size: 0.8rem;
    font-weight: ${props => props.isSelected ? '700' : '600'};
    cursor: pointer;
    background: ${props => props.isSelected
        ? (props.isDark ? 'rgba(76, 206, 172, 0.2)' : 'rgba(15, 118, 110, 0.15)')
        : 'transparent'};
    color: ${props => props.isSelected
        ? props.theme.palette.primary.main
        : (props.isDark ? '#e2e8f0' : '#1e293b')};
    border: 1px solid ${props => props.isSelected
        ? (props.isDark ? 'rgba(76, 206, 172, 0.4)' : 'rgba(15, 118, 110, 0.3)')
        : 'transparent'};
    transition: all 0.12s ease;
    outline: none;
    text-align: left;
    width: 100%;
    white-space: nowrap;

    &:hover {
        background: ${props => props.isSelected
            ? (props.isDark ? 'rgba(76, 206, 172, 0.28)' : 'rgba(15, 118, 110, 0.22)')
            : (props.isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)')};
    }
`;

const normalizeTfCode = (raw) => {
    if (!raw) return "";
    const s = String(raw).trim();
    const upper = s.toUpperCase();
    if (s === "5m" || upper === "5M" || upper === "M5") return "M5";
    if (s === "15m" || upper === "15M" || upper === "M15") return "M15";
    if (s === "1m" || (upper === "1M" && !upper.includes("MN")) || upper === "M1") return "M1";
    if (s === "3m" || upper === "M3") return "M3";
    if (s === "30m" || upper === "30M" || upper === "M30") return "M30";
    if (upper === "1H" || upper === "H1") return "H1";
    if (upper === "2H" || upper === "H2") return "H2";
    if (upper === "4H" || upper === "H4") return "H4";
    if (upper === "8H" || upper === "H8") return "H8";
    if (upper === "1D" || upper === "D1") return "D1";
    if (upper === "1W" || upper === "W1") return "W1";
    if (upper === "MN" || upper === "1MONTH" || upper === "MONTH") return "MN";
    if (upper === "3M") return "3M";
    if (upper === "6M") return "6M";
    if (upper === "1Y") return "1Y";
    return upper;
};

const isTfMatching = (valA, valB) => {
    if (!valA || !valB) return false;
    if (String(valA).trim().toUpperCase() === String(valB).trim().toUpperCase()) return true;
    return normalizeTfCode(valA) === normalizeTfCode(valB);
};

// Minimal, clean Timeframe Selector Component showing just the options
const TimeframeSelector = ({
    value,
    onChange,
    accent,
    label = "TF:",
    config = {}
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsMenuOpen(false);
            }
        };
        if (isMenuOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isMenuOpen]);

    const handleSelect = (tf) => {
        onChange(tf);
        setIsMenuOpen(false);
    };

    const options = config.options || config.recommendedTfs || ["1D", "4H", "1H", "15m", "5m"];
    const hasCurrent = options.some(opt => isTfMatching(value, opt));
    const displayOptions = hasCurrent ? options : (value ? [value, ...options] : options);

    return (
        <TimeframeControlContainer ref={dropdownRef} isOpen={isMenuOpen}>
            <TimeframeTriggerButton
                type="button"
                isOpen={isMenuOpen}
                bgColor={accent.badgeBg}
                accentColor={accent.icon}
                borderColor={accent.border}
                onClick={(e) => {
                    e.stopPropagation();
                    setIsMenuOpen(!isMenuOpen);
                }}
                title="Select timeframe"
            >
                <FiClock size={12} />
                <span>TF: <strong>{value || "Not set"}</strong></span>
                <FiChevronDown
                    size={11}
                    style={{
                        transform: isMenuOpen ? "rotate(180deg)" : "none",
                        transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
                    }}
                />
            </TimeframeTriggerButton>

            {isMenuOpen && (
                <TimeframeDropdownMenu isDark={isDark} onClick={(e) => e.stopPropagation()}>
                    {displayOptions.map((tf) => {
                        const isSelected = isTfMatching(value, tf);
                        return (
                            <TimeframeOptionItem
                                key={tf}
                                type="button"
                                isDark={isDark}
                                isSelected={isSelected}
                                onClick={() => handleSelect(tf)}
                            >
                                <span>{tf}</span>
                                {isSelected && <FiCheck size={13} />}
                            </TimeframeOptionItem>
                        );
                    })}
                </TimeframeDropdownMenu>
            )}
        </TimeframeControlContainer>
    );
};

const DrillDownMultiSelect = ({ categoryGroups, selectedIds, onToggle, onBack, onSave }) => {
    // Stack of { title: string, items: array }
    const [navStack, setNavStack] = useState([]);
    // const [direction, setDirection] = useState(1); // 1 = forward, -1 = back

    const currentLevel = navStack.length > 0 ? navStack[navStack.length - 1] : {
        title: "Select Category",
        items: categoryGroups.map(g => ({ ...g, isGroup: true, children: g.items || g.children }))
    };

    const handleItemClick = (item) => {
        if (item.children || item.items) {
            // setDirection(1);
            setNavStack([...navStack, { title: item.label || item.name, items: item.children || item.items }]);
        } else {
            onToggle(item.id);
            if (onSave) {
                onSave(); // Auto-save and return on selection
            }
        }
    };

    const handleBack = () => {
        // setDirection(-1);
        if (navStack.length > 0) {
            setNavStack(navStack.slice(0, -1));
        } else if (onBack) {
            onBack();
        }
    };

    // Helper to check selection depth
    const isSelected = (id) => selectedIds.includes(id);

    const showHeader = navStack.length > 0 || onBack;
    const showTitle = currentLevel.title !== "Select Category";

    return (
        <NavContainer>
            {showHeader && (
                <NavHeader>
                    {/* Back Button or Spacer */}
                    {(navStack.length > 0 || onBack) ? (
                        <NavBackBtn onClick={handleBack}>
                            <FiArrowLeft /> Back
                        </NavBackBtn>
                    ) : (
                        <div style={{ width: 40 }} />
                    )}

                    {showTitle ? <NavTitle>{currentLevel.title}</NavTitle> : <div />}

                    {/* Right spacer for balance (Save button removed) */}
                    <div style={{ width: 40 }} />
                </NavHeader>
            )}

            <NavGrid>
                {currentLevel.items.map((item, idx) => {
                    const active = isSelected(item.id);
                    // Check if group has selected children to highlight it
                    const hasSelectedChild = (node) => {
                        if (selectedIds.includes(node.id)) return true;
                        const kids = node.children || node.items;
                        if (kids) return kids.some(hasSelectedChild);
                        return false;
                    };
                    const groupActive = (item.children || item.items) && hasSelectedChild(item);

                    return (
                        <NavTile
                            key={item.id || item.name || idx}
                            isActive={active || groupActive}
                            onClick={() => handleItemClick(item)}
                        >
                            {item.label || item.name}
                            {(item.children || item.items) && <FiChevronDown style={{ transform: 'rotate(-90deg)', marginLeft: 6, opacity: 0.5 }} />}
                        </NavTile>
                    );
                })}
            </NavGrid>
        </NavContainer>
    );
};

const TextAreaWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const ScrollControls = styled.div`
  position: absolute;
  right: 4px;
  top: 4px;
  bottom: 4px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 2px;
  pointer-events: none;
`;

const ScrollButton = styled.div`
  pointer-events: auto;
  cursor: pointer;
  color: ${props => tokens(props.theme.palette.mode).grey[100]};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  transition: all 0.2s;
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.8)'};
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'};

  &:hover {
    color: ${props => tokens(props.theme.palette.mode).blueAccent[500]};
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.95)'};
  }
  
  svg {
    width: 16px;
    height: 16px;
    fill: currentColor;
  }
`;

const StyledNarrativeArea = styled.textarea`
    width: 100%;
    min-height: 32px;
    height: 32px;
    background: transparent;
    border: none;
    border-bottom: 1px solid ${props => tokens(props.theme.palette.mode).grey[700]};
    color: ${props => tokens(props.theme.palette.mode).grey[100]};
    padding: 6px 28px 6px 4px;
    font-family: inherit;
    font-size: 0.85rem;
    resize: none;
    line-height: 1.4;
    transition: all 0.2s;
    
    /* Hide scrollbar */
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
    
    &:focus {
        outline: none;
        border-bottom-color: ${props => props.theme.palette.primary.main};
        background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)'};
    }

    &:disabled {
        background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)'};
        opacity: 0.75;
        border-color: transparent;
        border-radius: 6px;
        padding: 6px 8px;
        resize: none;
    }
    
    &::placeholder {
        color: ${props => props.theme.palette.mode === 'dark' ? tokens(props.theme.palette.mode).grey[500] : tokens(props.theme.palette.mode).grey[400]};
        font-style: italic;
        font-size: 0.8rem;
    }
`;

const NarrativeArea = ({ value, onChange, placeholder, disabled }) => {
    const textareaRef = useRef(null);
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        if (textareaRef.current) {
            const { scrollHeight, clientHeight } = textareaRef.current;
            setShowScroll(scrollHeight > clientHeight);
        }
    }, [value]);

    const handleScroll = (direction) => {
        if (textareaRef.current) {
            textareaRef.current.scrollTop += direction * 24;
        }
    };

    return (
        <TextAreaWrapper>
            <StyledNarrativeArea
                ref={textareaRef}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                disabled={disabled}
                rows={1}
            />
            {showScroll && !disabled && (
                <ScrollControls>
                    <ScrollButton onClick={(e) => { e.stopPropagation(); handleScroll(-1); }} title="Scroll Up">
                        <svg viewBox="0 0 24 24">
                            <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z" />
                        </svg>
                    </ScrollButton>
                    <ScrollButton onClick={(e) => { e.stopPropagation(); handleScroll(1); }} title="Scroll Down">
                        <svg viewBox="0 0 24 24">
                            <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
                        </svg>
                    </ScrollButton>
                </ScrollControls>
            )}
        </TextAreaWrapper>
    );
};

const SubmitSection = styled.div`
    margin: 40px auto 20px auto;
    max-width: 700px;
    width: 100%;
    
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    padding: 24px 32px;
    
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(30, 35, 48, 0.6)' : 'rgba(255, 255, 255, 0.9)'};
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
    border-radius: 20px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    backdrop-filter: blur(12px);

    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s;
    
    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
        border-color: ${props => props.theme.palette.primary.main};
    }
`;

const TradeDropdown = ({ trades, selectedTradeId, onSelect }) => {
    const theme = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedTrade = trades.find(t => t.id === parseInt(selectedTradeId));
    const label = selectedTrade
        ? `#${selectedTrade.id} - ${selectedTrade.asset} (${selectedTrade.date}) - ${selectedTrade.side}`
        : "Select a trade...";

    return (
        <div ref={dropdownRef} style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
            <DropdownTrigger onClick={() => setIsOpen(!isOpen)}>
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {label}
                </span>
                {isOpen ? <FiChevronUp /> : <FiChevronDown />}
            </DropdownTrigger>

            {isOpen && (
                <ScrollableListContainer style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 100,
                    marginTop: '4px',
                    maxHeight: '300px',
                    background: theme.palette.mode === 'dark' ? '#1e2130' : '#ffffff', // Ensure opacity for stacking
                    boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
                }}>
                    {trades.length > 0 ? (
                        ['Active', 'Ready', 'Done'].map(group => {
                            const groupTrades = trades.filter(t => {
                                if (group === 'Active') return t.status === 'Active';
                                if (group === 'Ready') return t.status === 'Ready';
                                return t.status === 'Done';
                            }).sort((a, b) => a.id - b.id);

                            if (groupTrades.length === 0) return null;

                            return (
                                <div key={group}>
                                    <CategoryHeader>{group}</CategoryHeader>
                                    {groupTrades.map(trade => {
                                        const isSelected = trade.id === parseInt(selectedTradeId);
                                        return (
                                            <ListItem
                                                key={trade.id}
                                                selected={isSelected}
                                                isEditing={true}
                                                onClick={() => {
                                                    onSelect(trade.id);
                                                    setIsOpen(false);
                                                }}
                                            >
                                                <span>#{trade.id} - {trade.asset} ({trade.date}) - {trade.side}</span>
                                                {isSelected && <FiCheck size={14} />}
                                            </ListItem>
                                        );
                                    })}
                                </div>
                            );
                        })
                    ) : (
                        <div style={{ padding: '12px', fontSize: '0.9rem', opacity: 0.7, textAlign: 'center' }}>
                            No trades found
                        </div>
                    )}
                </ScrollableListContainer>
            )}
        </div>
    );
};




const JsonViewBtn = styled.button`
    background: transparent;
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
    color: ${props => tokens(props.theme.palette.mode).grey[400]};
    border-radius: 6px;
    padding: 2px 6px;
    font-size: 0.7rem;
    font-family: 'JetBrains Mono', monospace;
    cursor: pointer;
    transition: all 0.2s;
    margin-left: 8px;

    &:hover {
        background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};
        color: ${props => props.theme.palette.primary.main};
        border-color: ${props => props.theme.palette.primary.main};
    }
`;

// --- Tree View Components ---

// --- Tree View Components ---
// Imported from ../../components/TreeView

const KeyLevelsTable = ({ categoryGroups, levelData, onToggleLevel, isEditing, onSave }) => {
    const theme = useTheme();
    const [activeLevelId, setActiveLevelId] = useState(null);

    const levels = [
        { id: "scenario_start", label: "Start" },
        { id: "scenario_end", label: "End" },
        { id: "scenario_invalidation", label: "Invalidation" }
    ];

    const findItemWithPath = (groups, targetId, path = []) => {
        for (const group of groups) {
            const currentPath = [...path, group.label || group.name];
            if (group.id === targetId) return { item: group, path: path };

            const children = group.children || group.items;
            if (children) {
                const result = findItemWithPath(children, targetId, currentPath);
                if (result) return result;
            }
        }
        return null;
    };

    if (isEditing) {
        if (activeLevelId) {
            const selectedIds = levelData[activeLevelId] || [];
            return (
                <DrillDownMultiSelect
                    categoryGroups={categoryGroups}
                    selectedIds={selectedIds}
                    onToggle={(id) => onToggleLevel(activeLevelId, id)}
                    onBack={() => setActiveLevelId(null)}
                    onSave={() => setActiveLevelId(null)} // Save returns to level list
                />
            );
        }

        return (
            <NavContainer style={{ minHeight: 'auto', border: 'none', background: 'transparent' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {levels.map(lvl => {
                        const lvlSelectedIds = levelData[lvl.id] || [];
                        const hasSelection = lvlSelectedIds.length > 0;

                        return (
                            <NavTile
                                key={lvl.id}
                                isActive={false}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '8px 12px',
                                    cursor: 'default',
                                    height: 'auto',
                                    minHeight: '48px',
                                    overflow: 'visible'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0px', flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', height: '24px' }}>
                                        <span style={{ fontSize: '0.85rem', fontWeight: 600, minWidth: '80px', color: tokens(theme.palette.mode).grey[100] }}>
                                            {lvl.label}
                                        </span>
                                        <div style={{
                                            width: '1px',
                                            height: '24px',
                                            background: 'rgba(255, 255, 255, 0.2)',
                                            margin: '0 12px 0 4px'
                                        }} />
                                    </div>

                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', flex: 1, alignContent: 'center', paddingTop: '1px' }}>
                                        {hasSelection ? (
                                            lvlSelectedIds.map(id => {
                                                const result = findItemWithPath(categoryGroups, id);
                                                if (!result) return null;
                                                return (
                                                    <StyledChip key={id} style={{ fontSize: '0.75rem', padding: '2px 8px' }}>
                                                        {result.item.label}
                                                    </StyledChip>
                                                );
                                            })
                                        ) : (
                                            <span style={{ fontSize: '0.8rem', opacity: 0.5, fontStyle: 'italic', lineHeight: '24px' }}>None</span>
                                        )}
                                    </div>
                                </div>

                                <AddIconBtn onClick={() => setActiveLevelId(lvl.id)} title={`Edit ${lvl.label}`}>
                                    <FiPlus size={14} />
                                </AddIconBtn>
                            </NavTile>
                        );
                    })}
                </div>
            </NavContainer>
        );
    }

    // View Mode matching Edit Mode style but read-only
    const hasAnySelection = levels.some(lvl => (levelData[lvl.id] || []).length > 0);
    if (!hasAnySelection) {
        return (
            <span style={{ fontSize: '0.85rem', color: '#666', fontStyle: 'italic', padding: '4px 0' }}>
                None configured
            </span>
        );
    }

    return (
        <NavContainer style={{ minHeight: 'auto', border: 'none', background: 'transparent', padding: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {levels.map(lvl => {
                    const selectedIds = levelData[lvl.id] || [];
                    const hasSelection = selectedIds.length > 0;

                    if (!hasSelection) return null;

                    return (
                        <NavTile
                            key={lvl.id}
                            isActive={false}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '8px 12px',
                                cursor: 'default',
                                height: 'auto',
                                minHeight: '48px',
                                overflow: 'visible'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0px', flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', height: '24px' }}>
                                    <span style={{ fontSize: '0.85rem', fontWeight: 600, minWidth: '80px', color: tokens(theme.palette.mode).grey[100] }}>
                                        {lvl.label}
                                    </span>
                                    <div style={{
                                        width: '1px',
                                        height: '24px',
                                        background: 'rgba(255, 255, 255, 0.2)',
                                        margin: '0 12px 0 4px'
                                    }} />
                                </div>

                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', flex: 1, alignContent: 'center', paddingTop: '1px' }}>
                                    {hasSelection ? (
                                        selectedIds.map(id => {
                                            const result = findItemWithPath(categoryGroups, id);
                                            if (!result) return null;
                                            return (
                                                <StyledChip key={id} style={{ fontSize: '0.75rem', padding: '2px 8px' }}>
                                                    {result.item.label}
                                                </StyledChip>
                                            );
                                        })
                                    ) : (
                                        <span style={{ fontSize: '0.8rem', opacity: 0.5, fontStyle: 'italic', lineHeight: '24px' }}>None</span>
                                    )}
                                </div>
                            </div>
                        </NavTile>
                    );
                })}
            </div>
        </NavContainer>
    );
};

const EXECUTION_ZONE_CATEGORIES = [
    {
        name: "Confirmation",
        items: [
            {
                id: "conf_group_shift",
                label: "Structure Shift",
                children: [
                    { id: "conf_shift_choch", label: "Structural Shift (Minor Shift)" },
                    { id: "conf_shift_break_retest", label: "Break & Retest" }
                ]
            },
            {
                id: "conf_group_pattern",
                label: "Chart Pattern",
                children: [
                    { id: "conf_pattern_hs", label: "Head & Shoulders" },
                    { id: "conf_pattern_double_top_bottom", label: "Double Top / Bottom" }
                ]
            }
        ]
    },
    {
        name: "Entry Model",
        items: [
            {
                id: "entry_group_trigger",
                label: "Trigger",
                children: [
                    { id: "entry_trigger_full_body_close", label: "Full-Body Close" }
                ]
            },
            {
                id: "entry_group_candle",
                label: "Candlestick Pattern",
                children: [
                    { id: "entry_candle_marubozu", label: "Marubozu" },
                    { id: "entry_candle_momentum", label: "Momentum" },
                    { id: "entry_candle_engulfing", label: "Engulfing" }
                ]
            }
        ]
    }
];

const EXTENDED_MARKET_STRUCTURE_DATA = {
    ...MARKET_STRUCTURE_DATA,
    "htf_step_up": {
        label: "HTF Context",
        subtitle: "Context that encompasses the bias",
        description: "The market structure that encompasses bias or The higher-timeframe context in which the bias develops.",
        categories: MARKET_STRUCTURE_DATA.htf_step_up.categories
    },
    "bias_tf_analysis": {
        label: "Bias",
        subtitle: "Structure to trade",
        description: "Structure you are going to trade: uptrend, range, expansion, consolidation.",
        categories: MARKET_STRUCTURE_DATA.bias_tf_analysis.categories
    },
    "value_decision_zone": {
        label: "Observation Zone",
        subtitle: "Setup Identification",
        description: "Leg, pullback, structure shift, working zone.",
        categories: MARKET_STRUCTURE_DATA.value_decision_zone.categories
    },
    "execution_zone": {
        label: "Execution Zone",
        subtitle: "Confirmation & Entry",
        description: "Entry, SL, internal structure, fine shift: Confirmation (Structure Shift, Chart Pattern) and Entry Model (Trigger, Candlestick Pattern).",
        categories: EXECUTION_ZONE_CATEGORIES
    },
    "scenario": {
        label: "Key Levels",
        subtitle: "Start / End / Exit levels",
        description: "Key points of your scenario: starting level (Start), target objective (End), and invalidation / Stop-Loss level.",
        categories: MARKET_STRUCTURE_DATA.scenario.categories
    }
};


const DEFAULT_TIMEFRAMES = {
    htf_context: "1D",
    structural_bias: "4H",
    observation_zone: "1H",
    execution_zone: "5m"
};

const CARD_TF_CONFIG = {
    htf_step_up: {
        tfKey: "htf_context",
        icon: IconGlobe,
        defaultTf: "1D",
        tfLabel: "TF:",
        options: ["1M", "1W", "1D", "4H"],
        accent: {
            light: { bg: "#f1f5f9", icon: "#334155", border: "rgba(100, 116, 139, 0.35)", pillActive: "#1e293b", badgeBg: "rgba(100, 116, 139, 0.12)" },
            dark: { bg: "rgba(148, 163, 184, 0.14)", icon: "#94a3b8", border: "rgba(148, 163, 184, 0.3)", pillActive: "#cbd5e1", badgeBg: "rgba(148, 163, 184, 0.15)" }
        }
    },
    bias_tf_analysis: {
        tfKey: "structural_bias",
        icon: IconCompass,
        defaultTf: "4H",
        tfLabel: "TF:",
        options: ["1W", "1D", "4H", "1H"],
        accent: {
            light: { bg: "#e0e7ff", icon: "#4338ca", border: "rgba(99, 102, 241, 0.4)", pillActive: "#4338ca", badgeBg: "rgba(99, 102, 241, 0.12)" },
            dark: { bg: "rgba(99, 102, 241, 0.16)", icon: "#818cf8", border: "rgba(99, 102, 241, 0.35)", pillActive: "#818cf8", badgeBg: "rgba(99, 102, 241, 0.18)" }
        }
    },
    value_decision_zone: {
        tfKey: "observation_zone",
        icon: IconScanTarget,
        defaultTf: "1H",
        tfLabel: "TF:",
        options: ["4H", "1H", "30m", "15m"],
        accent: {
            light: { bg: "#ccfbf1", icon: "#0f766e", border: "rgba(20, 184, 166, 0.4)", pillActive: "#0f766e", badgeBg: "rgba(20, 184, 166, 0.12)" },
            dark: { bg: "rgba(20, 184, 166, 0.16)", icon: "#2dd4bf", border: "rgba(20, 184, 166, 0.35)", pillActive: "#2dd4bf", badgeBg: "rgba(20, 184, 166, 0.18)" }
        }
    },
    execution_zone: {
        tfKey: "execution_zone",
        icon: IconExecutionDial,
        defaultTf: "5m",
        tfLabel: "TF:",
        options: ["15m", "5m", "3m", "1m"],
        accent: {
            light: { bg: "#ffedd5", icon: "#c2410c", border: "rgba(245, 158, 11, 0.45)", pillActive: "#c2410c", badgeBg: "rgba(245, 158, 11, 0.12)" },
            dark: { bg: "rgba(245, 158, 11, 0.16)", icon: "#fbbf24", border: "rgba(245, 158, 11, 0.35)", pillActive: "#fbbf24", badgeBg: "rgba(245, 158, 11, 0.18)" }
        }
    },
    scenario: {
        icon: IconScenarioFlag,
        accent: {
            light: { bg: "#f3e8ff", icon: "#7e22ce", border: "rgba(168, 85, 247, 0.4)", pillActive: "#7e22ce", badgeBg: "rgba(168, 85, 247, 0.12)" },
            dark: { bg: "rgba(168, 85, 247, 0.16)", icon: "#c084fc", border: "rgba(168, 85, 247, 0.35)", pillActive: "#c084fc", badgeBg: "rgba(168, 85, 247, 0.18)" }
        }
    }
};

const TRACKER_STEPS = [
    {
        id: "0_htf",
        number: "0",
        label: "HTF Context",
        icon: IconGlobe,
        tfKey: "htf_context",
        defaultTf: "1D",
        isFilled: (data) => (data.htf_step_up?.concepts?.length > 0) || !!data.htf_step_up?.narrative
    },
    {
        id: "1_bias",
        number: "1",
        label: "Bias",
        icon: IconCompass,
        tfKey: "structural_bias",
        defaultTf: "4H",
        isFilled: (data) => (data.bias_tf_analysis?.concepts?.length > 0) || !!data.bias_tf_analysis?.narrative
    },
    {
        id: "2_obs",
        number: "2",
        label: "Observation Zone",
        icon: IconScanTarget,
        tfKey: "observation_zone",
        defaultTf: "1H",
        isFilled: (data) => (data.value_decision_zone?.concepts?.length > 0) || !!data.value_decision_zone?.narrative
    },
    {
        id: "3_exec",
        number: "3",
        label: "Execution Zone",
        icon: IconExecutionDial,
        tfKey: "execution_zone",
        defaultTf: "5m",
        isFilled: (data) => (data.execution_zone?.concepts?.length > 0) || !!data.execution_zone?.narrative || (data.confirmation_signals?.concepts?.length > 0) || (data.entry_model?.concepts?.length > 0)
    },
    {
        id: "4_scenario",
        number: "4",
        label: "Key Levels",
        icon: IconScenarioFlag,
        isFilled: (data) => (data.scenario?.levels && Object.values(data.scenario.levels).some(arr => arr && arr.length > 0)) || !!data.scenario?.narrative
    }
];

const ChartAnalysis = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [analysisData, setAnalysisData] = useState({});
    const [timeframes, setTimeframes] = useState(DEFAULT_TIMEFRAMES);
    const [originalState, setOriginalState] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [trades, setTrades] = useState([]);
    const [selectedTradeId, setSelectedTradeId] = useState("");

    // Modal State
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [submittedScoreInfo, setSubmittedScoreInfo] = useState(null);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const [activeDetail, setActiveDetail] = useState(null);
    const [activeJsonSection, setActiveJsonSection] = useState(null);
    const [showDictionary, setShowDictionary] = useState(false);

    const contentRef = useRef(null);
    const scrollRailYRef = useRef(null);
    const [thumbHeight, setThumbHeight] = useState("20%");
    const [thumbTop, setThumbTop] = useState("0%");
    const [showScrollY, setShowScrollY] = useState(false);
    const isDraggingY = useRef(false);
    const startY = useRef(0);
    const startScrollTop = useRef(0);

    useEffect(() => {
        const savedData = localStorage.getItem("marketStructurePipeline");
        if (savedData) {
            try {
                const parsed = JSON.parse(savedData);
                // Backward compatibility: if market_context exists in savedData, initialize htf_step_up and bias_tf_analysis
                if (parsed.market_context) {
                    if (!parsed.htf_step_up) parsed.htf_step_up = { ...parsed.market_context };
                    if (!parsed.bias_tf_analysis) parsed.bias_tf_analysis = { ...parsed.market_context };
                }
                // Migrate confirmation_signals & entry_model to execution_zone if not present
                if (!parsed.execution_zone && (parsed.confirmation_signals || parsed.entry_model)) {
                    parsed.execution_zone = {
                        concepts: [
                            ...(parsed.confirmation_signals?.concepts || []),
                            ...(parsed.entry_model?.concepts || [])
                        ],
                        narrative: [
                            parsed.confirmation_signals?.narrative,
                            parsed.entry_model?.narrative
                        ].filter(Boolean).join(" | ")
                    };
                }
                setAnalysisData(parsed);
                if (parsed.timeframes) {
                    setTimeframes(prev => ({ ...prev, ...parsed.timeframes }));
                }
            } catch (e) {
                console.error("Failed to parse saved analysis data", e);
            }
        }

        fetch(`${API_URL}/trades`)
            .then(res => res.json())
            .then(data => {
                const sortedTrades = data.sort((a, b) => new Date(b.date) - new Date(a.date));
                setTrades(sortedTrades);
            })
            .catch(err => console.error("Failed to fetch trades:", err));
    }, []);

    // Scroll Logic
    const handleScroll = () => {
        if (contentRef.current) {
            if (isDraggingY.current) return;

            const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
            const maxScrollTop = scrollHeight - clientHeight;

            if (maxScrollTop > 0) {
                const scrollPercentageY = (scrollTop / maxScrollTop) * 100;
                const maxTop = 100 - parseFloat(thumbHeight);
                const top = (scrollPercentageY / 100) * maxTop;
                setThumbTop(`${top}% `);
            }
        }
    };

    useEffect(() => {
        const checkScroll = () => {
            if (contentRef.current) {
                const { scrollHeight, clientHeight } = contentRef.current;
                const hasScrollY = scrollHeight > clientHeight;
                setShowScrollY(hasScrollY);
                if (hasScrollY) {
                    const heightPerc = (clientHeight / scrollHeight) * 100;
                    const finalHeight = Math.max(heightPerc, 10);
                    setThumbHeight(`${finalHeight}% `);
                }
            }
        };

        checkScroll();
        window.addEventListener("resize", checkScroll);
        // Also check when data changes (e.g., expanding text areas)
        const observer = new MutationObserver(checkScroll);
        if (contentRef.current) {
            observer.observe(contentRef.current, { childList: true, subtree: true, attributes: true });
        }

        return () => {
            window.removeEventListener("resize", checkScroll);
            observer.disconnect();
        };
    }, [analysisData]); // Re-check when data changes


    const handleMouseDownY = (e) => {
        e.preventDefault();
        e.stopPropagation();
        isDraggingY.current = true;
        startY.current = e.clientY;
        if (contentRef.current) {
            startScrollTop.current = contentRef.current.scrollTop;
        }
        document.addEventListener('mousemove', handleMouseMoveY);
        document.addEventListener('mouseup', handleMouseUpY);
    };

    const handleMouseMoveY = (e) => {
        if (!isDraggingY.current || !contentRef.current) return;
        const deltaY = e.clientY - startY.current;
        const { scrollHeight, clientHeight } = contentRef.current;
        // const maxScrollTop = scrollHeight - clientHeight;
        // const maxTop = 100 - parseFloat(thumbHeight); // in %

        // Calculate how much 1px of mouse movement corresponds to scroll
        // The track height is effectively clientHeight
        // But we are moving the thumb which moves in %
        // 1% of track = X pixels?
        // Let's simplify:
        // thumb moves deltaY pixels
        // track height is clientHeight
        // movement percentage = (deltaY / clientHeight) * 100
        // But we need to scale this to the scrollable area
        // It's easier to map thumb position to scrollTop

        // Improved logic:
        // New Thumb Top (px) = Old Thumb Top (px) + deltaY
        // But we store in %.

        // Let's just use the ratio:
        // ratio = scrollHeight / clientHeight
        // scrollTop += deltaY * ratio

        const ratio = scrollHeight / clientHeight;
        contentRef.current.scrollTop = startScrollTop.current + deltaY * ratio;
    };

    const handleMouseUpY = () => {
        isDraggingY.current = false;
        document.removeEventListener('mousemove', handleMouseMoveY);
        document.removeEventListener('mouseup', handleMouseUpY);
    };


    const [showResetModal, setShowResetModal] = useState(false);
    const [collapsedSections, setCollapsedSections] = useState({}); // Track locally saved/collapsed sections during edit mode

    const handleEditToggle = () => {
        if (!isEditing) {
            setOriginalState({
                data: JSON.parse(JSON.stringify(analysisData)),
                timeframes: { ...timeframes }
            });
            setCollapsedSections({}); // Reset collapses when entering edit mode
        }
        setIsEditing(!isEditing);
    };

    const handleSaveSection = (key) => {
        setCollapsedSections(prev => ({ ...prev, [key]: true }));
    };

    const handleReopenSection = (key) => {
        if (isEditing) {
            setCollapsedSections(prev => {
                const newState = { ...prev };
                delete newState[key];
                return newState;
            });
        }
    };

    const handleSave = () => {
        const payload = {
            ...analysisData,
            timeframes: timeframes
        };
        localStorage.setItem("marketStructurePipeline", JSON.stringify(payload));
        setIsEditing(false);
        setOriginalState(null);
    };

    const handleCancel = () => {
        if (originalState) {
            if (originalState.data) {
                setAnalysisData(originalState.data);
            } else {
                setAnalysisData(originalState);
            }
            if (originalState.timeframes) {
                setTimeframes(originalState.timeframes);
            }
        }
        setIsEditing(false);
        setOriginalState(null);
    };

    const handleResetClick = () => {
        setShowResetModal(true);
    };

    const handleConfirmReset = () => {
        setShowResetModal(false);
        const emptyState = {};
        Object.keys(EXTENDED_MARKET_STRUCTURE_DATA).forEach(key => {
            emptyState[key] = { concepts: [], narrative: "" };
        });
        setAnalysisData(emptyState);
        setTimeframes(DEFAULT_TIMEFRAMES);
    };

    const handleTimeframeChange = (tfKey, newTf) => {
        setTimeframes(prev => {
            const updated = {
                ...prev,
                [tfKey]: newTf
            };
            try {
                const stored = localStorage.getItem("marketStructurePipeline");
                const parsed = stored ? JSON.parse(stored) : {};
                localStorage.setItem("marketStructurePipeline", JSON.stringify({
                    ...parsed,
                    timeframes: updated
                }));
            } catch (e) {
                console.error("Failed to persist timeframe to localStorage", e);
            }
            return updated;
        });
    };

    const toggleConcept = (sectionKey, conceptId) => {
        if (!isEditing) return;

        setAnalysisData(prev => {
            const sectionData = prev[sectionKey] || { concepts: [], narrative: "" };
            const currentConcepts = sectionData.concepts || [];

            let newConcepts;
            if (currentConcepts.includes(conceptId)) {
                newConcepts = currentConcepts.filter(id => id !== conceptId);
            } else {
                newConcepts = [...currentConcepts, conceptId];

                // --- HTF Context Constraints Removed (Obsolete with new structure) ---
            }

            return {
                ...prev,
                [sectionKey]: {
                    ...sectionData,
                    concepts: newConcepts
                }
            };
        });
    };

    const toggleKeyLevel = (sectionKey, levelKey, conceptId) => {
        if (!isEditing) return;
        setAnalysisData(prev => {
            const sectionData = prev[sectionKey] || { concepts: [], narrative: "", levels: {} };
            const levels = sectionData.levels || {};
            const currentLevelConcepts = levels[levelKey] || [];

            let newLevelConcepts;
            if (currentLevelConcepts.includes(conceptId)) {
                newLevelConcepts = currentLevelConcepts.filter(id => id !== conceptId);
            } else {
                newLevelConcepts = [...currentLevelConcepts, conceptId];
            }

            return {
                ...prev,
                [sectionKey]: {
                    ...sectionData,
                    levels: {
                        ...levels,
                        [levelKey]: newLevelConcepts
                    }
                }
            };
        });
    };





    const handleNarrativeChange = (sectionKey, text) => {
        setAnalysisData(prev => ({
            ...prev,
            [sectionKey]: {
                ...(prev[sectionKey] || { concepts: [] }),
                narrative: text
            }
        }));
    };

    const handleSubmitClick = () => {
        if (isEditing) {
            setErrorMessage("Please save your changes before submitting.");
            setShowErrorModal(true);
            return;
        }

        if (!selectedTradeId) {
            setErrorMessage("Please select a trade to link this analysis to.");
            setShowErrorModal(true);
            return;
        }
        setShowConfirmModal(true);
    };

    const handleConfirmSubmit = async () => {
        setShowConfirmModal(false);

        const selectedTrade = trades.find(t => t.id === parseInt(selectedTradeId));
        if (!selectedTrade) return;

        try {
            // Mapping Logic:
            // "Context" = Summary of HTF Context, Structure, Liquidity, Area of Interest (AOI), Timing, Confluence, Risk
            // "Trigger" = Summary of Entry Model

            const contextSections = ["htf_step_up", "bias_tf_analysis", "value_decision_zone", "execution_zone", "scenario"];
            const exitSections = [];

            const formatSectionSummary = (sectionKey) => {
                const data = analysisData[sectionKey];
                if (!data) return null;
                const concepts = data.concepts || [];
                const narrative = data.narrative || "";

                // Find labels for concepts
                const sectionConfig = EXTENDED_MARKET_STRUCTURE_DATA[sectionKey];

                // Helper to find item and path recursively (reused logic)
                const findItemWithPath = (groups, targetId, path = []) => {
                    for (const group of groups) {
                        const currentPath = [...path, group.label || group.name];
                        if (group.id === targetId) return { item: group, path: path };

                        const children = group.children || group.items;
                        if (children) {
                            const foundChild = children.find(c => c.id === targetId);
                            if (foundChild) {
                                return { item: foundChild, path: currentPath };
                            }
                            const recursiveResult = findItemWithPath(children, targetId, currentPath);
                            if (recursiveResult) return recursiveResult;
                        }
                    }
                    return null;
                };

                let conceptLabelsString = "";

                if (sectionKey === "scenario" && data.levels) {
                    const lvlMap = [
                        { id: "scenario_start", label: "Start" },
                        { id: "scenario_end", label: "End" },
                        { id: "scenario_invalidation", label: "Invalidation" }
                    ];
                    const lvlLines = [];

                    lvlMap.forEach(lvl => {
                        const ids = data.levels[lvl.id] || [];
                        if (ids.length > 0) {
                            const labels = ids.map(id => {
                                const res = findItemWithPath(sectionConfig.categories, id);
                                return res ? res.item.label : id;
                            });
                            lvlLines.push(`${lvl.label}: ${labels.join(', ')}`);
                        }
                    });
                    conceptLabelsString = lvlLines.join("\n");
                } else {
                    conceptLabelsString = concepts.map(id => {
                        const result = findItemWithPath(sectionConfig.categories, id);
                        if (result) {
                            // For standard sections, show full path for context
                            const pathString = result.path.join(": ");
                            return `${pathString}: ${result.item.label}`;
                        }
                        return id;
                    }).join("\n");
                }

                const lines = [];
                if (conceptLabelsString) lines.push(`[${sectionConfig.label}]:\n${conceptLabelsString}`);
                if (narrative) lines.push(`> ${narrative}`);

                if (lines.length === 0) return null;
                return lines.join("\n");
            };

            const aggregateSections = (keys) => {
                const result = {};
                keys.forEach(key => {
                    // getSectionTreeData(key) now returns { [Label]: { data } }
                    // So we merge it directly into result
                    Object.assign(result, getSectionTreeData(key));
                });
                return result; // Return object, don't stringify yet
            };

            const contextData = aggregateSections(contextSections);
            const execTree = getSectionTreeData("execution_zone");
            const triggerData = {
                "Execution Trigger": execTree["Execution Zone"]?.["Entry Model"] || {}
            };

            // Create global JSON structure grouped by phase
            const setupJson = JSON.stringify({
                "timeframes": timeframes,
                "regions": {
                    "0_htf_context": {
                        "timeframe": timeframes.htf_context,
                        ...getSectionTreeData("htf_step_up")
                    },
                    "1_structural_bias": {
                        "timeframe": timeframes.structural_bias,
                        ...getSectionTreeData("bias_tf_analysis")
                    },
                    "2_observation_zone": {
                        "timeframe": timeframes.observation_zone,
                        ...getSectionTreeData("value_decision_zone")
                    },
                    "3_execution_zone": {
                        "timeframe": timeframes.execution_zone,
                        ...getSectionTreeData("execution_zone")
                    },
                    "4_scenario": {
                        ...getSectionTreeData("scenario")
                    }
                },
                "context": contextData,
                "trigger": triggerData
            }, null, 2);

            // Text summaries for other columns (keep flat string logic for these if needed, or update similarly)
            // For now, let's keep the existing formatSectionSummary logic for the text columns
            // const contextText = contextSections.map(formatSectionSummary).filter(Boolean).join("\n\n");
            // const triggerText = triggerSections.map(formatSectionSummary).filter(Boolean).join("\n\n");
            const exitText = exitSections.map(formatSectionSummary).filter(Boolean).join("\n\n");

            // Calculate planned R:R if entry/sl/tp already exist on the trade (or null if empty)
            const entryP = parseFloat(selectedTrade?.entry);
            const slP = parseFloat(selectedTrade?.sl);
            const tpP = parseFloat(selectedTrade?.tp);
            const rrPlanned = (entryP && slP && tpP && entryP !== slP)
                ? Math.abs(tpP - entryP) / Math.abs(entryP - slP)
                : null;

            // Trigger Setup Score calculation on submit now that JSON is ready
            const setupResult = calculateSetupScore(setupJson, rrPlanned);
            setSubmittedScoreInfo(setupResult);

            const updatedTrade = {
                ...selectedTrade,
                view: setupJson, // Renamed from market_structure
                exit_reason: exitText,
                setup_score: Number((setupResult.score / 100).toFixed(2))
            };

            const response = await fetch(`${API_URL}/trades/${selectedTradeId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatedTrade),
            });

            if (response.ok) {
                setShowSuccessModal(true);
                setTimeout(() => setShowSuccessModal(false), 2000);
            } else {
                const errorData = await response.json();
                alert(`Failed to update trade: ${errorData.detail || "Unknown error"}`);
            }
        } catch (error) {
            console.error("Error submitting trade plan:", error);
            alert("An error occurred while submitting the trade plan.");
        }
    };

    const getSectionTreeData = (key) => {
        const sectionState = analysisData[key] || {};
        const config = EXTENDED_MARKET_STRUCTURE_DATA[key];
        if (!config) return {};

        // Recursive helper to find item and its parent path
        const findItemWithPath = (groups, targetId, path = []) => {
            for (const group of groups) {
                const currentLabel = group.label || group.name;

                if (group.id === targetId) {
                    return { item: group, path: path }; // Return path leading TO this item
                }

                const children = group.children || group.items;
                if (children) {
                    // If diving deeper, add current group to path
                    const result = findItemWithPath(children, targetId, [...path, currentLabel]);
                    if (result) return result;
                }
            }
            return null;
        };

        const formatHierarchical = (selectedIds) => {
            const tree = {};

            selectedIds.forEach(id => {
                const result = findItemWithPath(config.categories, id);
                if (result) {
                    // Combine full path including the item itself
                    const fullPath = [...result.path, result.item.label];
                    fullPath.forEach((part, index) => {
                        const isLast = index === fullPath.length - 1;

                        if (isLast) {
                            // Leaf node logic:
                            // We use an array to store leaves if multiple items land here,
                            // OR if we want to represent it as a value.
                            // However, the previous level 'part' (parent category) holds this.
                            // Actually, fullPath includes 'Markup'. 'Uptrend' holds 'Markup'.

                            // Let's look at the PARENT level.
                            // We are Iterating parts. 'Uptrend'.
                            // Next part is 'Markup'.

                            // Re-think: We want to insert `result.item.label` at `result.path`.
                        }
                    });

                    // Recursive insertion helper
                    const insert = (node, pathParts, itemLabel) => {
                        const [head, ...tail] = pathParts;
                        if (!head) {
                            // We are at the container for the item?
                            // Wait, pathParts are categories.
                            // If pathParts is empty, we are at root?? No.
                            return;
                        }

                        // If we have tail, 'head' is a category group
                        if (tail.length > 0) {
                            if (!node[head]) node[head] = {};
                            // Ensure it's an object to hold children (unless conflict?)
                            // If node[head] is array (leaf container), we might have conflict.
                            // Assumption: A category name won't be both a group and a leaf in `selectedIds`.
                            if (Array.isArray(node[head])) {
                                // Conflict: Convert array to object with numeric keys? Or special key?
                                // For now assume structure consistency.
                                node[head] = { "_items": node[head] };
                            }
                            insert(node[head], tail, itemLabel);
                        } else {
                            // 'head' is the last category (the direct parent of the item)
                            // We want to add itemLabel to node[head]
                            if (!node[head]) node[head] = [];
                            if (!Array.isArray(node[head])) {
                                // It's an object (has subgroups). Add item to special key?
                                if (!node[head]["_others"]) node[head]["_others"] = [];
                                node[head]["_others"].push(itemLabel);
                            } else {
                                node[head].push(itemLabel);
                            }
                        }
                    };

                    // Root call
                    if (result.path.length === 0) {
                        // Item at root?
                        if (!tree["General"]) tree["General"] = [];
                        tree["General"].push(result.item.label);
                    } else {
                        insert(tree, result.path, result.item.label);
                    }

                } else {
                    if (!tree["Unknown"]) tree["Unknown"] = [];
                    tree["Unknown"].push(id);
                }
            });
            return tree;
        };

        if (key === "scenario") {
            const levels = [
                { id: "scenario_start", label: "Start" },
                { id: "scenario_end", label: "End" },
                { id: "scenario_invalidation", label: "Invalidation" }
            ];

            const formattedLevels = {};
            const levelData = sectionState.levels || {};

            levels.forEach(lvl => {
                const selectedIds = levelData[lvl.id] || [];
                // Apply hierarchical formatting to each Scenario Level
                const groupedItems = formatHierarchical(selectedIds);
                // If only one group, maybe flatten? Or keep consistent?
                // Let's keep consistent: { "Liquidity": ["Internal > Gap"] }
                if (selectedIds.length > 0) {
                    formattedLevels[lvl.label] = groupedItems;
                } else {
                    formattedLevels[lvl.label] = "None";
                }
            });

            return {
                [config.label]: {
                    ...formattedLevels,
                    Details: sectionState.narrative || ""
                }
            };
        }

        // Standard sections
        const selectedIds = sectionState.concepts || [];
        const structuredData = formatHierarchical(selectedIds);

        return {
            [config.label]: {
                ...structuredData,
                Details: sectionState.narrative || ""
            }
        };
    };

    const renderSectionCard = (key) => {
        const tab = EXTENDED_MARKET_STRUCTURE_DATA[key];
        const tfConfig = CARD_TF_CONFIG[key];
        const sectionState = analysisData[key] || { concepts: [], narrative: "" };
        const selectedIds = sectionState.concepts || [];
        const hasSelection = selectedIds.length > 0;
        const isSectionEditing = isEditing && !collapsedSections[key];

        return (
            <SectionBlock key={key}>
                <SectionTitle>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        {tfConfig && tfConfig.icon && (
                            <CardIconBadge
                                bgColor={isDark ? tfConfig.accent.dark.bg : tfConfig.accent.light.bg}
                                iconColor={isDark ? tfConfig.accent.dark.icon : tfConfig.accent.light.icon}
                                borderColor={isDark ? tfConfig.accent.dark.border : tfConfig.accent.light.border}
                            >
                                <tfConfig.icon size={20} color={isDark ? tfConfig.accent.dark.icon : tfConfig.accent.light.icon} />
                            </CardIconBadge>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span>{tab.label}</span>
                                {hasSelection && <FiCheckCircle size={14} color={tokens('dark').greenAccent[400]} />}
                            </div>
                            <span style={{ fontSize: '0.72rem', fontWeight: 400, opacity: 0.6, textTransform: 'none' }}>
                                {tab.subtitle}
                            </span>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {tfConfig && tfConfig.tfKey && (
                            <TimeframeSelector
                                value={timeframes[tfConfig.tfKey] || tfConfig.defaultTf}
                                onChange={(newTf) => handleTimeframeChange(tfConfig.tfKey, newTf)}
                                accent={isDark ? tfConfig.accent.dark : tfConfig.accent.light}
                                label={tfConfig.tfLabel}
                                config={tfConfig}
                            />
                        )}
                        <JsonViewBtn
                            onClick={() => setActiveJsonSection(key)}
                            title="View Raw Data"
                        >
                            {`{ }`}
                        </JsonViewBtn>
                        <InfoIconWrapper onClick={() => setActiveDetail(key)}>
                            <FiInfo size={16} />
                        </InfoIconWrapper>
                    </div>
                </SectionTitle>

                {activeDetail === key && (
                    <DetailsOverlay onClick={() => setActiveDetail(null)}>
                        {tfConfig && tfConfig.icon && (
                            <CardIconBadge
                                bgColor={isDark ? tfConfig.accent.dark.bg : tfConfig.accent.light.bg}
                                iconColor={isDark ? tfConfig.accent.dark.icon : tfConfig.accent.light.icon}
                                borderColor={isDark ? tfConfig.accent.dark.border : tfConfig.accent.light.border}
                                style={{ width: '46px', height: '46px', marginBottom: '12px' }}
                            >
                                <tfConfig.icon size={26} color={isDark ? tfConfig.accent.dark.icon : tfConfig.accent.light.icon} />
                            </CardIconBadge>
                        )}
                        <SectionTitle style={{ border: 'none', padding: 0, marginBottom: '12px', justifyContent: 'center' }}>
                            {tab.label}
                        </SectionTitle>
                        <OverlayText>{tab.description}</OverlayText>
                        <span style={{ marginTop: '16px', fontSize: '0.8rem', color: '#666', fontStyle: 'italic' }}>
                            (Click anywhere to close)
                        </span>
                    </DetailsOverlay>
                )}

                <ConceptsContainer style={{ flex: 1 }}>
                    {key === "scenario" ? (
                        <KeyLevelsTable
                            categoryGroups={tab.categories}
                            levelData={sectionState.levels || {}}
                            onToggleLevel={(lvl, id) => toggleKeyLevel(key, lvl, id)}
                            isEditing={isSectionEditing}
                            onSave={() => handleSaveSection(key)}
                        />
                    ) : isSectionEditing ? (
                        <DrillDownMultiSelect
                            categoryGroups={tab.categories}
                            selectedIds={selectedIds}
                            onToggle={(id) => toggleConcept(key, id)}
                            onSave={() => handleSaveSection(key)}
                        />
                    ) : (
                        <ChipsContainer
                            onClick={() => handleReopenSection(key)}
                            style={{ cursor: isEditing ? 'pointer' : 'default', minHeight: '40px' }}
                        >
                            {hasSelection ? (
                                selectedIds.map(id => {
                                    const findItemWithPath = (groups, targetId, path = []) => {
                                        for (const group of groups) {
                                            const currentPath = [...path, group.label || group.name];

                                            if (group.id === targetId) return { item: group, path: path };

                                            const children = group.children || group.items;
                                            if (children) {
                                                const foundChild = children.find(c => c.id === targetId);
                                                if (foundChild) {
                                                    return { item: foundChild, path: currentPath };
                                                }
                                                const recursiveResult = findItemWithPath(children, targetId, currentPath);
                                                if (recursiveResult) return recursiveResult;
                                            }
                                        }
                                        return null;
                                    };

                                    const result = findItemWithPath(tab.categories, id);
                                    if (!result) return null;

                                    const { item, path } = result;
                                    const pathString = path.join(": ");

                                    const label = (key === "htf_step_up" || key === "bias_tf_analysis" || key === "scenario" || key === "value_decision_zone" || key === "confirmation_signals" || key === "execution_zone")
                                        ? `${pathString}: ${item.label}`
                                        : item.label;

                                    return (
                                        <StyledChip key={item.id}>
                                            {label}
                                        </StyledChip>
                                    );
                                })
                            ) : (
                                <span style={{ fontSize: '0.85rem', color: '#666', fontStyle: 'italic', padding: '4px 0' }}>
                                    {isEditing ? "Click to add..." : "None selected"}
                                </span>
                            )}
                            {isEditing && (
                                <AddIconBtn title="Add more...">
                                    <FiPlus size={14} />
                                </AddIconBtn>
                            )}
                        </ChipsContainer>
                    )}
                </ConceptsContainer>

                <div style={{ marginTop: 'auto' }}>
                    <NarrativeArea
                        placeholder={isEditing ? "Notes..." : "No notes."}
                        value={sectionState.narrative || ""}
                        onChange={(e) => handleNarrativeChange(key, e.target.value)}
                        disabled={!isEditing}
                    />
                </div>
            </SectionBlock>
        );
    };

    return (
        <PageContainer>
            {activeJsonSection && (
                <ModalOverlay onClick={() => setActiveJsonSection(null)}>
                    <ModalBox onClick={e => e.stopPropagation()} style={{ width: '500px', maxWidth: '90vw' }}>
                        <ModalTitle>
                            {EXTENDED_MARKET_STRUCTURE_DATA[activeJsonSection]?.label || activeJsonSection}
                        </ModalTitle>
                        <TreeContainer>
                            <TreeView data={getSectionTreeData(activeJsonSection)} isRoot={true} />
                        </TreeContainer>
                        <ModalActions>
                            <Btn onClick={() => setActiveJsonSection(null)} title="Close">
                                <MdClose size={18} />
                            </Btn>
                            <Btn primary onClick={() => {
                                navigator.clipboard.writeText(JSON.stringify(getSectionTreeData(activeJsonSection), null, 2));
                            }} title="Copy JSON">
                                <MdContentCopy size={18} />
                            </Btn>
                        </ModalActions>
                    </ModalBox>
                </ModalOverlay>
            )}

            {showConfirmModal && (
                <ModalOverlay onClick={() => setShowConfirmModal(false)}>
                    <ModalBox onClick={e => e.stopPropagation()}>
                        <ModalTitle>Confirm Submission</ModalTitle>
                        <ModalText>
                            Are you sure you want to link this analysis to trade #{selectedTradeId}?
                        </ModalText>
                        <ModalActions>
                            <Btn onClick={() => setShowConfirmModal(false)}>Cancel</Btn>
                            <Btn primary onClick={handleConfirmSubmit}>Confirm</Btn>
                        </ModalActions>
                    </ModalBox>
                </ModalOverlay>
            )}

            {showSuccessModal && (
                <ModalOverlay>
                    <ModalBox style={{ padding: '28px', maxWidth: '340px', textAlign: 'center' }}>
                        <FiCheckCircle size={44} color={tokens('dark').greenAccent[500]} style={{ marginBottom: '12px' }} />
                        <ModalTitle style={{ fontSize: '1.2rem', marginBottom: '8px' }}>Trade Plan Submitted!</ModalTitle>
                        {submittedScoreInfo && (
                            <div style={{ marginTop: '8px', fontSize: '0.9rem', color: '#b0bec5' }}>
                                Setup Score: <strong style={{ color: '#10b981' }}>{submittedScoreInfo.score}/100 ({submittedScoreInfo.grade})</strong>
                                <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '4px' }}>
                                    {submittedScoreInfo.rr_planned ? `Planned R:R: ${submittedScoreInfo.rr_planned}R` : 'Prices pending entry in Journal'}
                                </div>
                            </div>
                        )}
                    </ModalBox>
                </ModalOverlay>
            )}

            {showErrorModal && (
                <ModalOverlay onClick={() => setShowErrorModal(false)}>
                    <ModalBox onClick={e => e.stopPropagation()} style={{ borderColor: tokens('dark').redAccent[500] }}>
                        <MdClose size={48} color={tokens('dark').redAccent[500]} style={{ marginBottom: '16px' }} />
                        <ModalTitle>Error</ModalTitle>
                        <ModalText>{errorMessage}</ModalText>
                        <ModalActions>
                            <Btn onClick={() => setShowErrorModal(false)}>Close</Btn>
                        </ModalActions>
                    </ModalBox>
                </ModalOverlay>
            )}

            <TechnicalDictionaryModal
                isOpen={showDictionary}
                onClose={() => setShowDictionary(false)}
            />

            <ContentHeader>
                <ContentTitleBlock>
                    <Chip>Chart Analysis</Chip>
                    <Subtitle><em>Structured 5-region analysis framework — From macrostructural to execution & scenario.</em></Subtitle>
                </ContentTitleBlock>
            </ContentHeader>

            <ContentActionsRow>
                <BtnIcon
                    onClick={() => setShowDictionary(true)}
                    aria-label="Technical Dictionary"
                    title="Technical Dictionary"
                >
                    <FiBookOpen size={15} />
                </BtnIcon>

                <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.1)', margin: '0 4px' }} />

                {/* Global Edit/Save actions */}
                {!isEditing ? (
                    <BtnIcon onClick={handleEditToggle} aria-label="Edit Setup" title="Edit Setup">✎</BtnIcon>
                ) : (
                    <>
                        <Btn primary onClick={handleSave}>Save</Btn>
                        <Btn onClick={handleCancel}>Cancel</Btn>
                        <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.1)', margin: '0 4px' }} />
                        <Btn danger onClick={handleResetClick} style={{ marginLeft: '8px' }}>Reset</Btn>
                    </>
                )}
            </ContentActionsRow>

            {showResetModal && (
                <ModalOverlay onClick={() => setShowResetModal(false)}>
                    <ModalBox onClick={e => e.stopPropagation()} style={{ borderColor: tokens('dark').redAccent[500] }}>
                        <ModalTitle>Reset Setup?</ModalTitle>
                        <ModalText>
                            Are you sure you want to clear all selections and notes? This action cannot be undone unless you Cancel changes afterward.
                        </ModalText>
                        <ModalActions>
                            <Btn onClick={() => setShowResetModal(false)}>Cancel</Btn>
                            <Btn danger onClick={handleConfirmReset}>Yes, Clear All</Btn>
                        </ModalActions>
                    </ModalBox>
                </ModalOverlay>
            )}

            <MainContentArea>
                <ScrollableContent ref={contentRef} onScroll={handleScroll}>
                    <ContentWrapper>
                        {/* Top Workflow Tracker Bar */}
                        <WorkflowTrackerBar>
                            <TrackerPillsList>
                                <span style={{ fontSize: '0.82rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.8, marginRight: 4 }}>
                                    Flow:
                                </span>
                                {TRACKER_STEPS.map((step, idx) => {
                                    const isFilled = step.isFilled(analysisData);
                                    const tf = step.tfKey ? (timeframes[step.tfKey] || step.defaultTf) : null;
                                    return (
                                        <TrackerStepItem key={step.id} isCompleted={isFilled}>
                                            <TrackerStepBadge isCompleted={isFilled}>
                                                {isFilled ? "✓" : step.number}
                                            </TrackerStepBadge>
                                            {step.icon && <step.icon size={13} style={{ opacity: 0.9, flexShrink: 0 }} />}
                                            <span>{step.label}</span>
                                            {tf && <TrackerStepTf>{tf}</TrackerStepTf>}
                                            {idx < TRACKER_STEPS.length - 1 && <TrackerStepArrow>→</TrackerStepArrow>}
                                        </TrackerStepItem>
                                    );
                                })}
                            </TrackerPillsList>

                            <TrackerProgressCount>
                                {TRACKER_STEPS.filter(step => step.isFilled(analysisData)).length}/5
                            </TrackerProgressCount>
                        </WorkflowTrackerBar>

                        <SectionDivider />

                        <SplitLayout>
                            <LeftGrid>
                                {["htf_step_up", "bias_tf_analysis", "value_decision_zone", "execution_zone"].map(renderSectionCard)}
                            </LeftGrid>

                            <VerticalDivider />

                            <RightColumn>
                                {renderSectionCard("scenario")}
                            </RightColumn>
                        </SplitLayout>

                        <SubmitSection>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                                <span style={{ fontSize: '0.9rem', fontWeight: 600, color: tokens('dark').primary.main, whiteSpace: 'nowrap' }}>
                                    Link to Trade:
                                </span>
                                <TradeDropdown
                                    trades={trades}
                                    selectedTradeId={selectedTradeId}
                                    onSelect={setSelectedTradeId}
                                />
                            </div>

                            <Btn primary onClick={handleSubmitClick}>Submit</Btn>
                        </SubmitSection>
                    </ContentWrapper>
                </ScrollableContent>

                {showScrollY && (
                    <ScrollRailVertical ref={scrollRailYRef} onMouseDown={(e) => {
                        // Optional: Click rail to jump
                    }}>
                        <ScrollThumbVertical
                            height={thumbHeight}
                            top={thumbTop}
                            onMouseDown={handleMouseDownY}
                        />
                    </ScrollRailVertical>
                )}
            </MainContentArea>

        </PageContainer >
    );
};

export default ChartAnalysis;
